import type { admin } from '@repletoryreach/sdk-api';
import type { StatusTone } from '@repletoryreach/sdk-ui/data-display';

type Conversation = admin.AdminConversation;

/** The state codes `GET /v1/admin/conversations` emits. */
export type ConversationState = 'open' | 'pending' | 'resolved' | 'archived';

const STATES: ConversationState[] = ['open', 'pending', 'resolved', 'archived'];

/**
 * A thread's state, as the inbox records it.
 *
 * The row also carries `state_id`, the lookup row's key. That is a join key
 * and never a label, so nothing here reads it: `state` is the readable code
 * and the only thing rendered. It is optional on the wire for compatibility,
 * and a row without one gets no badge rather than a guess, because "unassigned
 * and open" and "unassigned and closed" are the two cases this column exists
 * to tell apart.
 */
export function stateOf(r: Conversation): ConversationState | null {
  const code = r.state?.toLowerCase();
  return code && (STATES as string[]).includes(code) ? (code as ConversationState) : null;
}

/** Blue while it is live, amber while it waits, green when done, grey when filed. */
export const STATE_TONE: Record<ConversationState, StatusTone> = {
  open: 'pending',
  pending: 'warning',
  resolved: 'healthy',
  archived: 'inactive'
};

export const STATE_LABEL_KEYS: Record<ConversationState, string> = {
  open: 'conversations.state.open',
  pending: 'conversations.state.pending',
  resolved: 'conversations.state.resolved',
  archived: 'conversations.state.archived'
};

/** Every state, for the filter drawer, in lifecycle order. */
export const ALL_STATES = STATES;
